import { z } from 'zod';

export const LOCATION_CREDENTIAL_SOURCES = {
  manual: 'manual',
  url: 'url',
} as const;

const locationCredentialSourceSchema = z.enum([LOCATION_CREDENTIAL_SOURCES.manual, LOCATION_CREDENTIAL_SOURCES.url]);
const storedLocationCredentialSchema = z.object({
  apiKey: z.string().trim().min(1),
  locationId: z.number().finite().optional(),
  locationName: z.string().trim().optional(),
  source: locationCredentialSourceSchema.optional(),
  validatedAt: z.string().trim().optional(),
});
const fallbackValidatedAt = new Date(0).toISOString();

export type LocationCredentialSource = z.infer<typeof locationCredentialSourceSchema>;

export interface LocationCredential {
  apiKey: string;
  id: string;
  locationId?: number;
  locationName: string;
  source: LocationCredentialSource;
  validatedAt: string;
}

export function createLocationCredentialId(apiKey: string) {
  return `location-api-key:${encodeURIComponent(apiKey.toLowerCase())}`;
}

export function maskLocationApiKey(apiKey: string) {
  const normalized = apiKey.trim();

  if (normalized.length <= 4) {
    return '••••';
  }

  return `•••• ${normalized.slice(-4)}`;
}

export function readStoredLocation(value: unknown): LocationCredential[] {
  const parsedLocation = storedLocationCredentialSchema.safeParse(value);

  if (!parsedLocation.success) {
    return [];
  }

  const location = parsedLocation.data;

  return [
    {
      apiKey: location.apiKey,
      id: createLocationCredentialId(location.apiKey),
      locationId: location.locationId,
      locationName: location.locationName || 'Location',
      source: location.source ?? LOCATION_CREDENTIAL_SOURCES.manual,
      validatedAt: location.validatedAt || fallbackValidatedAt,
    },
  ];
}

export function upsertLocationCredential(
  locations: LocationCredential[],
  input: {
    apiKey: string;
    locationId?: number;
    locationName: string;
    source: LocationCredentialSource;
  },
  now = new Date().toISOString(),
) {
  const { locationId, locationName, source } = input;
  const id = createLocationCredentialId(input.apiKey);
  const nextLocation: LocationCredential = {
    apiKey: input.apiKey,
    id,
    locationId,
    locationName: locationName.trim() || `Location ${maskLocationApiKey(input.apiKey)}`,
    source,
    validatedAt: now,
  };
  const existingLocation = locations.find(location => location.id === id);

  if (existingLocation) {
    return locations.map(location =>
      location.id === id
        ? {
            ...existingLocation,
            locationId,
            locationName: nextLocation.locationName,
            source,
            validatedAt: now,
          }
        : location,
    );
  }

  return [nextLocation, ...locations];
}
